import {create} from 'zustand';

// Stores the user's unwrapped RSA private key and per-workspace DEKs in
// memory only. Nothing here is ever serialized to localStorage or sent
// over the wire — closing the tab discards every key.
//
// SessionStorage is intentionally NOT used either: CryptoKey objects
// aren't serializable, and persisting their raw bytes would weaken the
// security model. The cost is that opening a new tab re-prompts the
// user — that's a deliberate trade-off, matching the threat model.

interface State {
  userPrivateKey: CryptoKey | null;
  setUserPrivateKey: (key: CryptoKey | null) => void;
  // workspaceId → DEK
  workspaceDeks: Map<number, CryptoKey>;
  setWorkspaceDek: (workspaceId: number, dek: CryptoKey) => void;
  forgetWorkspaceDek: (workspaceId: number) => void;
  // workspace IDs the user has been prompted about this session;
  // reset by `forgetAll` so the next login re-prompts cleanly
  promptedWorkspaces: Set<number>;
  markPrompted: (workspaceId: number) => void;
  // forget everything — call on logout
  forgetAll: () => void;
}

export const useKeyStore = create<State>(set => ({
  userPrivateKey: null,
  workspaceDeks: new Map(),
  promptedWorkspaces: new Set(),
  setUserPrivateKey: key => set({userPrivateKey: key}),
  setWorkspaceDek: (workspaceId, dek) =>
    set(state => {
      const next = new Map(state.workspaceDeks);
      next.set(workspaceId, dek);
      return {workspaceDeks: next};
    }),
  forgetWorkspaceDek: workspaceId =>
    set(state => {
      const next = new Map(state.workspaceDeks);
      next.delete(workspaceId);
      return {workspaceDeks: next};
    }),
  markPrompted: workspaceId =>
    set(state => {
      const next = new Set(state.promptedWorkspaces);
      next.add(workspaceId);
      return {promptedWorkspaces: next};
    }),
  forgetAll: () =>
    set({
      userPrivateKey: null,
      workspaceDeks: new Map(),
      promptedWorkspaces: new Set(),
    }),
}));

// Imperative helpers for non-React call sites (e.g. the upload strategy)
export const keyStore = {
  getUserPrivateKey: () => useKeyStore.getState().userPrivateKey,
  setUserPrivateKey: (key: CryptoKey | null) =>
    useKeyStore.getState().setUserPrivateKey(key),
  getWorkspaceDek: (workspaceId: number) =>
    useKeyStore.getState().workspaceDeks.get(workspaceId) ?? null,
  setWorkspaceDek: (workspaceId: number, dek: CryptoKey) =>
    useKeyStore.getState().setWorkspaceDek(workspaceId, dek),
  forgetWorkspaceDek: (workspaceId: number) =>
    useKeyStore.getState().forgetWorkspaceDek(workspaceId),
  isPrompted: (workspaceId: number) =>
    useKeyStore.getState().promptedWorkspaces.has(workspaceId),
  markPrompted: (workspaceId: number) =>
    useKeyStore.getState().markPrompted(workspaceId),
  forgetAll: () => useKeyStore.getState().forgetAll(),
};
